// @flow

import * as React from 'react';
import {useState, useEffect} from 'react';
import { css } from '@emotion/core';
import styled from '@emotion/styled';

import ScheduleGrid from './components/grid/ScheduleGrid';
import type {DateParts, Schedules} from "./components/Container";
import type {Airing, Channel, Episode, Show} from "./Objects";
import type {Client as ShowClient} from "../ShowPage/APIClient";
import type {Client as ScheduleClient, ScheduleAPIResponse} from "./APIClient";
import moment from "moment";
import Main from "./components/Main";
import {Controls} from "./components/header/Controls";
import {LargeCalendar} from "./components/Calendar";
import Sidebar from "./components/Sidebar";
import Calendar from "./components/Calendar";
import Container from "./components/Container";
import InnerPage from "../Core/components/InnerPage";
import CalendarStyles from "./components/CalendarStyles";
import {sortChannels} from "./helpers";
import { init as initGA } from "../Helpers/ga";
import DoNotUseMeAd from "./components/DoNotUseMeAd";
import PrintButton from "./components/Print";
import colors from "../Styles/colors";
import mq from "../Styles/breakpoints";
import ScheduleError from "./components/ScheduleError";

initGA();

type Props = {
  current: DateParts,
  getAiring: number => Airing | null,
  getEpisode: number => Episode | null,
  getShow: number => Show | null,
  schedules: Schedules,
  loadNextDay: () => Promise<void>,
  loadPrevDay: () => Promise<void>,
  moveToDate: (year: number, month: number, date: number) => Promise<void>,
  getShowAPIClient: () => ShowClient,
  firstCellRef: { current: null | React.ElementRef<any> },
  gridRef: { current: null | React.ElementRef<any> },
  changeChannel: (index: number) => void,
  currentChannel: number,
  onMonthChange: (number, number) => void,
  calendarMonth: number,
  calendarYear: number
}

export const APP_NAME = 'ScheduleV2';

let today = moment().tz('America/Chicago').startOf('day');
let twoWeeksAgo = today.subtract(2, 'weeks').unix();
let sixWeeksOut = today.add(8, 'weeks').unix();

function Inner({ current,
                 getAiring,
                 getEpisode,
                 getShow,
                 schedules,
                 loadNextDay,
                 loadPrevDay,
                 moveToDate,
                 getShowAPIClient,
                 firstCellRef,
                 gridRef,
                 changeChannel,
                 currentChannel,
                 onMonthChange,
                 calendarMonth,
                 calendarYear }: Props) {

  let { year, month, date } = current;
  let currentDate = new Date(year, month, date);
  let start = moment(currentDate).tz('America/Chicago').startOf('day').unix();

  const showError = ! schedules.size;

  return <React.Fragment>
    <Main>
      <CalendarStyles/>
      <Controls
        loadPrevDay={loadPrevDay}
        loadNextDay={loadNextDay}
        loadDate={ (year, month, date) => {
          return moveToDate(year, month, date);
        }}
        current={current}
        channelList={sortChannels([...schedules.keys()])}
        currentChannel={currentChannel}
        changeChannel={changeChannel}

        calendar={<LargeCalendar
          start={twoWeeksAgo}
          end={sixWeeksOut}
          current={currentDate}
          changeDate={d => moveToDate(d.getFullYear(), d.getMonth(), d.getDate())}
          month={calendarMonth}
          year={calendarYear}
          onMonthChange={onMonthChange}
        />}

        calendarMonth={calendarMonth}
        calendarYear={calendarYear}
      />
      <ScheduleGrid
        start={start}
        schedules={schedules}
        getAiring={getAiring}
        getEpisode={getEpisode}
        getShow={getShow}
        interval={30}
        firstCellRef={firstCellRef}
        gridRef={gridRef}
        getShowAPIClient={getShowAPIClient}
        changeChannel={changeChannel}
        currentChannel={currentChannel}
      />
      { showError ? <ScheduleError message="No schedule available for the selected date" /> : "" }
    </Main>
    <Sidebar>
      <Calendar
        start={twoWeeksAgo}
        end={sixWeeksOut}
        current={currentDate}
        changeDate={d => moveToDate(d.getFullYear(), d.getMonth(), d.getDate())}
        month={calendarMonth}
        year={calendarYear}
        onMonthChange={onMonthChange}
      />
      <PrintButton/>
      <DoNotUseMeAd suppressHydrationWarning={true} />
    </Sidebar>
  </React.Fragment>;
}

type AppProps = {|
  current: DateParts,
  schedule: ScheduleAPIResponse,
  showClient: () => ShowClient,
  scheduleClient: () => ScheduleClient,
  channel: Channel
|};

class App extends React.Component<AppProps> {
  render() {
    let { current, schedule, showClient, scheduleClient, channel } = this.props;

    let init = { date: current, data: schedule };

    let page = css`
      background: ${colors.coreColor1};
    `;

    let Layout = styled('div')`
      display: block;
      
      ${mq.large(`
        display: flex;
      `)}
      
      @media print {
        display: block;
      }
    `;

    return <InnerPage css={page}>
      <Layout>
        <Container init={init} showClient={showClient} scheduleClient={scheduleClient} initialChannel={channel}>
          {(props) => {
            return <Inner {...props} />;
          }}
        </Container>
      </Layout>
    </InnerPage>;
  }
}

export default App;